import {
  Clear as ClearIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
  PriorityHighRounded as PriorityHighRoundedIcon,
  Search as SearchIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { Dialog, Divider, InputAdornment, TextField } from "@mui/material";
import _ from "lodash";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Avatar from "../../../assets/images/avatar-df.png";
import ImportSc from "../../../assets/images/icons/import-employee-sc.png";
import Import from "../../../assets/images/icons/import-employee.png";
import {
  EmployeePresenceRecord,
  isTodayPresence,
  mergePresenceUpdate,
  PresenceMqttPayload,
  useEmployeePresenceMqtt,
} from "../../../hooks/useEmployeePresenceMqtt";
import useNoti from "../../../hooks/useNoti";
import { useTranslation } from "../../../i18n/useTranslation";
import { AppNotifications } from "../../../models";
import { EmployeeService } from "../../../services/employee.service";
import { checkLinkImage, hhmm_to_hhmmaMINUTE, renderErrorImage } from "../../../utils";
import FloatingBtn from "../../components/FloatingBtn";
import { EditIcon } from "../../components/Icons/EditIcon";
import { ImportIcon } from "../../components/Icons/ImportIcon";
import { RemoveIcon } from "../../components/Icons/RemoveIcon";
import Loading from "../../components/Loading";
import ModalFooter from "../../components/ModalFooter";
import NotificationModal from "../../components/NotificationModal";
import SimpleModalHeader from "../../components/SimpleModalHeader";
import {
  BASE_LINK_IMAGE,
  DEFAULT_IMG_ALT,
  DEFAULT_STORE_INFOS,
  TEXT_HOLDER,
} from "../../constants";
import { MESSAGES } from "../../constants/mock";
import { APP_ROUTES } from "../../constants/module";
import { ACTIVE_COLOR, DEFAULT_COLOR } from "../../core/Layout/routes";
import "./index.scss";

const STAFF_LIST_SCROLL_KEY = "staff-list-scroll-top";

const escapeCsvCell = (value: unknown) => {
  const text = String(value ?? "");
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const downloadCsv = (filename: string, rows: string[][]) => {
  const csv = `\uFEFF${rows.map((row) => row.map(escapeCsvCell).join(",")).join("\n")}\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const Staff: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [pushNoti] = useNoti();
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  // eslint-disable-next-line
  const [visibleSearch, setVisibleSearch] = useState<boolean>(false);
  const [notiDelete, setNotiDelete] = useState<{
    id: string;
    fullName: string;
  }>({ id: "", fullName: "" });
  const [listEmployee, setListEmployee] = useState<EmployeePresenceRecord[]>([]);
  const [presenceDate, setPresenceDate] = useState<string | null>(null);
  const [mqttTopic, setMqttTopic] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [importFile, setImportFile] = useState<boolean>(false);
  const [importSc, setImportSc] = useState<any>(null);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [checkErr, setCheckErr] = useState<boolean | string>(false);
  const [syncingDevices, setSyncingDevices] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const listRef = useRef<HTMLDivElement>(null);

  const saveListScrollPosition = useCallback(() => {
    const scrollTop = listRef.current?.scrollTop;
    if (scrollTop && scrollTop > 0) {
      sessionStorage.setItem(STAFF_LIST_SCROLL_KEY, String(scrollTop));
    }
  }, []);

  const onSearch = _.debounce((value) => {
    setSearchValue(value);

    value ? setVisibleSearch(true) : setVisibleSearch(false);
  }, 300);

  const fetchData = async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) {
        setLoading(true);
      }
      const result = await EmployeeService.getEmployeePresence();
      if (result.data.data) {
        setListEmployee(result.data.data.employees || []);
        setPresenceDate(result.data.data.date || null);
        setMqttTopic(result.data.data.mqttTopic || null);
      }
    } catch (err) {
      console.log(err);
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  };

  const handlePresenceUpdate = useCallback((payload: PresenceMqttPayload) => {
    setListEmployee((current) =>
      mergePresenceUpdate(current, payload, presenceDate)
    );
  }, [presenceDate]);

  useEmployeePresenceMqtt({
    mqttTopic,
    enabled: Boolean(mqttTopic),
    onUpdate: handlePresenceUpdate,
  });

  const onSiteCount = listEmployee.filter(
    (employee) =>
      isTodayPresence(employee.presence, presenceDate) &&
      employee.presence?.isOnSite
  ).length;

  const visibleEmployees = searchValue
    ? listEmployee.filter(
        (f) =>
          f.code?.toLowerCase().includes(searchValue.toLowerCase()) ||
          f.fullName?.toLowerCase().includes(searchValue.toLowerCase()) ||
          f.email?.toLowerCase().includes(searchValue.toLowerCase()),
      )
    : listEmployee;

  const exportEmployees = async () => {
    if (exporting) return;
    try {
      setExporting(true);
      const result = await EmployeeService.getEmployee();
      const directory = (result.data?.data || []) as any[];
      const byId = new Map(directory.map((row) => [String(row._id), row]));
      const rows = visibleEmployees.map((employee) => {
        const detail = byId.get(String(employee._id)) || {};
        const showTodayPresence = isTodayPresence(employee.presence, presenceDate);
        const isOnSite = showTodayPresence && Boolean(employee.presence?.isOnSite);
        return {
          code: employee.code || detail.code || "",
          fullName: employee.fullName || detail.fullName || "",
          checkinName: detail.checkinName || "",
          email: employee.email || detail.email || "",
          phone: employee.phone || detail.phone || "",
          team: employee.team?.name || detail.team?.name || "",
          position: employee.position?.name || detail.position?.name || "",
          level: detail.level || "",
          gender: detail.gender || "",
          dateOfBirth: detail.dateOfBirth || "",
          firstDayOfWork: detail.firstDayOfWork || "",
          lastWorkingDay: detail.lastWorkingDay || "",
          hiddenFromReport: employee.hiddenFromReport || detail.hiddenFromReport ? "Yes" : "",
          status: isOnSite ? t("staff.onSite") : t("staff.offSite"),
          checkIn:
            showTodayPresence && employee.presence?.checkIn
              ? employee.presence.checkIn
              : "",
        };
      });

      if (!rows.length) {
        pushNoti(AppNotifications.WARNING, t("staff.exportEmpty"));
        return;
      }

      downloadCsv(`employees_${new Date().toISOString().slice(0, 10)}.csv`, [
        [
          "Employee ID",
          t("staff.fullName"),
          t("staff.checkinName"),
          "Email",
          "Phone",
          t("staff.team"),
          t("staff.position"),
          t("staff.level"),
          "Gender",
          "Date of birth",
          "First day of work",
          "Last working day",
          "Hidden",
          t("common.status"),
          "Check-in",
        ],
        ...rows.map((row) => [
          row.code,
          row.fullName,
          row.checkinName,
          row.email,
          row.phone,
          row.team,
          row.position,
          row.level,
          row.gender,
          row.dateOfBirth,
          row.firstDayOfWork,
          row.lastWorkingDay,
          row.hiddenFromReport,
          row.status,
          row.checkIn,
        ]),
      ]);
      pushNoti(AppNotifications.SUCCESS, t("staff.exportSuccess"));
    } catch (error) {
      console.log(error);
      pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (loading || !listEmployee.length || !listRef.current) {
      return;
    }

    const savedScrollTop = sessionStorage.getItem(STAFF_LIST_SCROLL_KEY);
    if (!savedScrollTop) {
      return;
    }

    listRef.current.scrollTop = Number(savedScrollTop);
    sessionStorage.removeItem(STAFF_LIST_SCROLL_KEY);
  }, [loading, listEmployee.length]);

  const deleteEmployee = async (id: string) => {
    try {
      const res = await EmployeeService.deleteEmployee(id);
      if (res.status === 200 || res.status === 201) {
        pushNoti(AppNotifications.SUCCESS, MESSAGES.SUCCESS);
        setNotiDelete({ id: "", fullName: notiDelete.fullName });
        setListEmployee((current) => current.filter((employee) => employee._id !== id));
        fetchData({ silent: true });
      } else if (res && res.status === 400) {
        pushNoti(AppNotifications.ERROR, res.data?.message);
      } else {
        pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
      }
    } catch (error) {
      pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
      console.log(error);
    }
  };

  const onFileChange = (event: any) => {
    const fileExcel: File = event.target.files[0];
    setSelectedFile(fileExcel);
  };

  const checkFile = () => {
    const fileExcel = selectedFile?.name.slice(
      selectedFile?.name.lastIndexOf("."),
      selectedFile?.name.length
    );

    if (fileExcel !== ".xls" && fileExcel !== ".csv") {
      if (fileExcel !== ".xlsx") {
        return setCheckErr(
          "The uploaded file must have the extension .xls, .xlsx or .csv"
        );
      }
    }

    return setCheckErr(true);
  };

  useEffect(() => {
    selectedFile && checkFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile]);

  const triggerDeviceSync = async () => {
    try {
      setSyncingDevices(true);
      const res = await EmployeeService.triggerDeviceSync();
      if (res.status === 200 || res.status === 201) {
        const published = res.data?.data?.published;
        pushNoti(
          AppNotifications.SUCCESS,
          published
            ? t("staff.syncPublished")
            : t("staff.syncMqttPending")
        );
      } else {
        pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
      }
    } catch (error) {
      pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
    } finally {
      setSyncingDevices(false);
    }
  };

  const importEmployee = async () => {
    if (selectedFile === null) {
      return setCheckErr("You have not selected a file to upload");
    }
    if (checkErr) {
      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
        const res = await EmployeeService.importEmployee(formData);
        if (res.status === 200 || res.status === 201) {
          setImportFile(false);
          setImportSc(res.data?.data);
          if (res.data?.data?.errorFilePath) {
            window.open(BASE_LINK_IMAGE + res.data?.data.errorFilePath);
          }
          fetchData();
        } else if (res && res.status === 400) {
          pushNoti(AppNotifications.ERROR, res.data?.message);
        } else {
          pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
        }
      } catch (error) {
        pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
        console.log(error);
      }
    }
  };

  return (
    <div className='staff-listing'>
      <div className='staff-listing__content'>
        <div className='staff-listing__content__action'>
          <div className='staff-listing__content__action__title'>
            {t("staff.allTeams", {
              count: listEmployee?.length,
              online: onSiteCount,
            })}
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <TextField
              placeholder={t("staff.searchPlaceholder")}
              variant='outlined'
              className={"search-style"}
              inputProps={{ className: "input" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='large' />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => {
                onSearch(e.target.value);
              }}
            />
            <button
              className='btn-cancel-style btn--shadow'
              style={{ marginRight: 12 }}
              disabled={syncingDevices}
              onClick={triggerDeviceSync}
            >
              {syncingDevices ? t("staff.syncing") : t("staff.syncDevices")}
            </button>
            <button
              className='btn-cancel-style btn--shadow'
              style={{ marginRight: 12 }}
              disabled={exporting || !visibleEmployees.length}
              onClick={exportEmployees}
            >
              {exporting ? t("staff.exporting") : t("staff.export")}
            </button>
            <button
              className='btn-cancel-style btn--shadow'
              onClick={() =>
                setImportFile(true)
              }
            >
              <ImportIcon
                color={ACTIVE_COLOR}
                size={[24, 24]}
                viewBox={[24, 24]}
              />
              &emsp;Import
            </button>
          </div>
        </div>
        <div className='staff-listing__content__list' ref={listRef}>
          <div className='header-list-page'>
            <div className='staff-listing__content__list__status'>Status</div>
            <div className='staff-listing__content__list__id'>Employee ID</div>
            <div className='staff-listing__content__list__name'>Name</div>
            <div className='staff-listing__content__list__checkin'>Check-in</div>
            <div className='staff-listing__content__list__email'>Email</div>
            <div className='staff-listing__content__list__phone'>Phone</div>
            <div className='staff-listing__content__list__edit'></div>
            <div className='staff-listing__content__list__del'></div>
          </div>
          {!loading && visibleEmployees.length ? (
            visibleEmployees.map((employee: EmployeePresenceRecord) => {
              const showTodayPresence = isTodayPresence(
                employee.presence,
                presenceDate
              );
              const isOnSite =
                showTodayPresence && Boolean(employee.presence?.isOnSite);

              return (
                <div
                  key={employee._id}
                  className='item-list-page'
                  onClick={() => {
                    saveListScrollPosition();
                    history.push(
                      APP_ROUTES.STAFF_DETAIL.replace(":staffId", employee._id)
                    );
                  }}
                >
                  <div className='staff-listing__content__list__status'>
                    <span
                      className={`statusStaff ${
                        isOnSite ? "statusStaff--online" : "statusStaff--offline"
                      }`}
                      title={isOnSite ? t("staff.onSite") : t("staff.offSite")}
                    />
                  </div>
                  <div className='staff-listing__content__list__id'>
                    {employee.code}
                  </div>
                  <div className='staff-listing__content__list__name item-name'>
                    <img
                      src={checkLinkImage(employee.profilePhoto || "") || Avatar}
                      alt={DEFAULT_IMG_ALT}
                      onError={renderErrorImage()}
                    />
                    {employee.fullName}
                    {employee.hiddenFromReport ? (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#9a6b00",
                          background: "#fff4d6",
                          padding: "2px 6px",
                          borderRadius: 4,
                        }}
                      >
                        Khoá
                      </span>
                    ) : null}
                  </div>
                  <div className='staff-listing__content__list__checkin'>
                    {showTodayPresence && employee.presence?.checkIn
                      ? hhmm_to_hhmmaMINUTE(employee.presence.checkIn)
                      : TEXT_HOLDER}
                  </div>
                  <div className='staff-listing__content__list__email'>
                    {employee.email}
                  </div>
                  <div className='staff-listing__content__list__phone'>
                    {employee.phone || TEXT_HOLDER}
                  </div>
                  <div className='staff-listing__content__list__edit'>
                    <div
                      style={{ width: "fit-content" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        saveListScrollPosition();
                        history.push(
                          APP_ROUTES.STAFF_EDIT.replace(
                            ":staffId",
                            employee._id
                          )
                        );
                      }}
                    >
                      <EditIcon
                        color={DEFAULT_COLOR}
                        size={[18, 18]}
                        viewBox={[18, 18]}
                      />
                    </div>
                  </div>
                  <div className='staff-listing__content__list__del'>
                    <div
                      style={{ width: "fit-content" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setNotiDelete({
                          id: employee._id,
                          fullName: employee.fullName,
                        });
                      }}
                    >
                      <RemoveIcon
                        color={"#272B2F"}
                        size={[18, 18]}
                        viewBox={[18, 18]}
                      />
                    </div>
                  </div>
                </div>
              );
            })
          ) : loading ? (
            <Loading />
          ) : (
            <div className='staff-listing__empty'>No employees found.</div>
          )}
        </div>
      </div>

      <FloatingBtn
        visible={false}
        onClickPrimary={() => {
          history.push(APP_ROUTES.STAFF_CREATE);
        }}
        btns={[]}
      />

      <NotificationModal
        visible={notiDelete.id !== ""}
        title={"Delete Employee"}
        subTitle={
          <span>
            Are you sure you want to delete employee
            <span style={{ fontWeight: 700 }}> {notiDelete?.fullName}</span>?
          </span>
        }
        icon={
          <div className={`notification-icon-box red`}>
            <PriorityHighRoundedIcon
              style={{ fontSize: "7rem", color: "#fff" }}
            />
          </div>
        }
        actions={
          <>
            <button
              className='btn-cancel-style'
              onClick={() => {
                setNotiDelete({ id: "", fullName: notiDelete.fullName });
              }}
            >
              Cancel
            </button>
            <button
              className='btn-confirm-style'
              onClick={() => {
                deleteEmployee(notiDelete.id);
              }}
            >
              Delete
            </button>
          </>
        }
        onClose={() => {
          setNotiDelete({ id: "", fullName: notiDelete.fullName });
        }}
      />

      <Dialog open={importFile} maxWidth='md'>
        <div className='dialog-import-employee'>
          <SimpleModalHeader
            title={"Import Employees"}
            onClose={() => {
              setImportFile(false);
              setSelectedFile(null);
            }}
          />
          <div className='dialog-import-employee__content'>
            <div className='dialog-import-employee__content__file'>
              <div>
                Only Excel (.xls and .xlsx) and .csv file types are supported.
              </div>
              <a
                href={DEFAULT_STORE_INFOS.FILE_EXCEL_IMPORT_EMPLOYEE}
                download='sample-file.xlsx'
                target='_blank'
                rel='noreferrer'
                className='dialog-import-employee__content__file__link-color'
              >
                Download Sample File
              </a>
            </div>
            <div className='dialog-import-employee__content__upload'>
              <div className='dialog-import-employee__content__upload__img'>
                <img src={Import} alt={DEFAULT_IMG_ALT} />
                <input type='file' onChange={onFileChange} />
              </div>
              {selectedFile && (
                <div className='selected-file'>
                  <div className='selected-file__name'>
                    <InsertDriveFileOutlinedIcon
                      fontSize='large'
                      style={{ marginBottom: -3, marginRight: 10 }}
                    />
                    {selectedFile?.name}
                  </div>
                  <div
                    onClick={() => {
                      setSelectedFile(null);
                      setCheckErr(false);
                    }}
                  >
                    <DeleteOutlineOutlinedIcon
                      fontSize='large'
                      style={{ marginBottom: -5, cursor: "pointer" }}
                    />
                  </div>
                </div>
              )}
              {!selectedFile && (
                <div className='dialog-import-employee__content__input'>
                  <div>Drag your file here to import employees</div>
                  <div className='dialog-import-employee__content__input__line'>
                    <Divider
                      style={{ borderColor: "#BDC6D7", padding: 1, width: 61 }}
                    />
                    &nbsp;or&nbsp;
                    <Divider
                      style={{ borderColor: "#BDC6D7", padding: 1, width: 61 }}
                    />
                  </div>
                  <button className='btn-cancel-style'>Browse files</button>
                  <input type='file' onChange={onFileChange} />
                </div>
              )}
            </div>
            {typeof checkErr === "string" && (
              <div className='err'>
                <ErrorIcon style={{ marginBottom: 2, marginRight: 8 }} />
                {checkErr}
              </div>
            )}
          </div>
          <ModalFooter
            formId='import-staff-form'
            showDeleteButton={""}
            handleDeleteAction={() => {}}
            handleCancelAction={() => {
              setImportFile(false);
              setSelectedFile(null);
            }}
            title={"Import"}
            handleSubmit={() => {
              importEmployee();
            }}
          />
        </div>
      </Dialog>

      <Dialog open={importSc !== null} maxWidth='sm'>
        <div className='dialog-import-sc'>
          <div
            className='dialog-import-sc__clear'
            onClick={() => {
              setImportSc(null);
            }}
          >
            <ClearIcon fontSize='large' />
          </div>
          <div className='dialog-import-sc__content'>
            <img src={ImportSc} alt={DEFAULT_IMG_ALT} />
            <div className='dialog-import-sc__content__title'>
              Successfully Imported
            </div>
            <div className='dialog-import-sc__content__table'>
              <div className='dialog-import-sc__content__table__row'>
                <div>Import successfully :</div>
                <div className='dialog-import-sc__content__table__row__val'>
                  {importSc?.succeed}
                </div>
              </div>
              <div className='dialog-import-sc__content__table__row'>
                <div>Duplicate employee :</div>
                <div className='dialog-import-sc__content__table__row__val'>
                  {importSc?.skip}
                </div>
              </div>
              <div className='dialog-import-sc__content__table__row'>
                <div>Import Error : </div>
                <div className='dialog-import-sc__content__table__row__val'>
                  {importSc?.error}
                </div>
              </div>
            </div>
          </div>
          <div className='dialog-import-sc__btn'>
            <button
              className='btn-cancel-style'
              onClick={() => {
                setImportSc(null);
                setImportFile(true);
                setSelectedFile(null);
              }}
            >
              Import an other file
            </button>
            <button
              className='btn-confirm-style'
              onClick={() => {
                setImportSc(null);
              }}
            >
              Done
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Staff;
